"use client";

import Link from "next/link";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Button } from "@shared/src/components/ui/button";
import { MdKeyboardArrowDown } from "react-icons/md";
import { CgMenuGridO } from "react-icons/cg";
import { GoldBarsIcon, WalletIcon } from "@shared/src/components/assets/icons";
import { NotificationsModal } from "@shared/src/components/common/dialogs/NotificationsModal";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@shared/src/components/ui/sheet";
import { FaucetModal } from "@shared/src/components/common/dialogs/FaucetModal";
import { UserAccountModal } from "@shared/src/components/common/dialogs/UserAccountModal";
import { CreateAdButton } from "@shared/src/components/common/buttons/CreateAdButton";
import { useAuthStore } from "@shared/src/stores/useAuthStore";
import { SignInButton } from "@shared/src/components/common/buttons/SignInButton";
import { usePathname, useRouter } from "next/navigation";
import { NetworkAvatar } from "@shared/src/components/common/avatars/NetworkAvatar";
import { useMediaQuery } from "@shared/src/hooks/useMediaQuery";
import { useNotificationStore } from "@shared/src/stores/useNotificationStore";
import { useGoldBars } from "@shared/src/hooks/useGoldBars";
import { Skeleton } from "@shared/src/components/ui/skeleton";
import { WarningBanner } from "@shared/src/components/common/misc/WarningBanner";
import { IS_MAINNET } from "@shared/src/constants";
import { IxLogo } from "@shared/src/components/assets/logos";
import { shortenAddress } from "@shared/src/utils/helpers";
import { useAdminAuthStore } from "@shared/src/stores/useAdminAuthStore";
import { ADMIN_ROUTE } from "@shared/src/config/page-links";
import { CustomDropdown } from "./top-navbar/CustomDropdown";
import { IconButton, MenuDivider } from "./top-navbar/primitives";
import { NotificationIcon } from "./top-navbar/NotificationIcon";
import { Menu, LogoDropdown } from "./top-navbar/Menus";
import { cn } from "@shared/src/utils/cn";
import { usePreventNumberInputScroll } from "./top-navbar/hooks";
import { navigationConfig } from "@shared/src/config/navigation";
import { RiDropFill } from "react-icons/ri";

// (Helper subcomponents & hooks extracted to ./top-navbar/*)

export function TopNavbar() {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [isFaucetOpen, setIsFaucetOpen] = useState(false);
  const [isUserAccountOpen, setIsUserAccountOpen] = useState(false);
  const [isNotificationSheetOpen, setIsNotificationSheetOpen] = useState(false);
  const { activeAccount } = useAuthStore();
  const pathname = usePathname();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const isAboveXl = useMediaQuery("(min-width: 1280px)");
  const { goldBars, isLoading } = useGoldBars();
  const notifications = useNotificationStore(state => state.orderNotifications);
  const router = useRouter();
  const adminUser = useAdminAuthStore(state => state.adminUser);

  const currentPath = pathname.split("/")[1];
  const isAdminPage = currentPath === "admin";

  const isAuthenticatedUser = activeAccount?.isAuthenticated && !isAdminPage;

  // Filter navigation based on authentication
  const filteredNavigation = useMemo(() => {
    return navigationConfig.filter(item => {
      if (item.href === "/p2p-orders") {
        return isAuthenticatedUser;
      }
      return true;
    });
  }, [isAuthenticatedUser]);

  // Separate navigation items based on viewport and current path
  const { mainLinks, linkItems, dropdownItems, helpLink, ordersItem } = useMemo(() => {
    // Get all link type items (Quick Trade, P2P, ixPay)
    const links = filteredNavigation.filter(item => item.type === "link");

    // Only find current path in link items, not in all navigation
    const currentPathItem = isAboveXl
      ? null
      : links.find(item => item.href === pathname) || filteredNavigation.find(item => item.href === "/");

    // Get Help and Orders items
    const helpItem = filteredNavigation.find(item => item.href === "/help" && item.type === "dropdown");
    const orders = filteredNavigation.find(item => item.href === "/p2p-orders");

    return {
      mainLinks: currentPathItem ? [currentPathItem] : [],
      linkItems: isAboveXl ? links : links.filter(item => item.href !== currentPathItem?.href),
      dropdownItems: links.filter(item => item.href !== currentPathItem?.href),
      helpLink: helpItem,
      ordersItem: orders,
    };
  }, [filteredNavigation, pathname, isAboveXl]);

  const closeAllModals = useCallback(() => {
    setOpenDropdown(null);
    setIsNotificationSheetOpen(false);
  }, []);

  // Use the custom hooks
  // useScrollLock(!!openDropdown);
  usePreventNumberInputScroll();

  // Close all dropdowns when pathname changes
  useEffect(() => {
    closeAllModals();
  }, [pathname, closeAllModals]);

  // Handle synchronized transitions between dropdown and sheet
  useEffect(() => {
    if (!isDesktop && openDropdown === "dropdown-notifications") {
      setOpenDropdown(null);
      setIsNotificationSheetOpen(true);
    } else if (isDesktop && isNotificationSheetOpen) {
      setIsNotificationSheetOpen(false);
      setOpenDropdown("dropdown-notifications");
    }
  }, [isDesktop, openDropdown, isNotificationSheetOpen]);

  // Pass closeAllModals to ordersItem content
  const ordersContent = useMemo(() => {
    if (!ordersItem?.customContent) return null;
    return React.createElement(ordersItem.customContent, {
      onNavigate: closeAllModals,
    });
  }, [ordersItem, closeAllModals]);

  const handleAdminSignOut = () => {
    router.push(ADMIN_ROUTE);
    useAdminAuthStore.getState().clearAdminUser();
  };

  return (
    <nav className="sticky top-0 z-50">
      {!IS_MAINNET && <WarningBanner />}
      <div className="bg-surfaces-bg-primary border-b border-border-secondary">
        {/* {openDropdown && <div className="fixed inset-0 bg-black-50 backdrop-blur-sm" />} */}
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Link
                href="/"
                className={cn("flex items-center", openDropdown === "dropdown-logo" ? "z-50" : "")}
                onClick={closeAllModals}
              >
                <IxLogo />
              </Link>
              <CustomDropdown
                showVersion={true}
                trigger={
                  <Button variant="ghost-clean" size="sm" className="p-0">
                    <MdKeyboardArrowDown
                      size={24}
                      className={`transition-transform duration-200 ${
                        openDropdown === "dropdown-logo" ? "rotate-180" : ""
                      }`}
                    />
                  </Button>
                }
                content={<LogoDropdown onCloseParentDropdown={() => setOpenDropdown(null)} />}
                dropdownId="dropdown-logo"
                openDropdown={openDropdown}
                setOpenDropdown={setOpenDropdown}
              />
            </div>

            <MenuDivider className="max-md:hidden" />

            {/* Navigation Links and Dropdowns */}
            <div className="hidden md:flex items-center gap-4">
              {/* Current pathname with dropdown */}
              <div className="flex items-center gap-4">
                {mainLinks.map(item => (
                  <div key={item.href} className="flex items-center">
                    <div className={cn("flex items-center", openDropdown === "dropdown-nav" ? "z-50" : "")}>
                      <Link href={item.href} className="text-text-primary font-bold" onClick={closeAllModals}>
                        {item.label}
                      </Link>
                      {/* Dropdown trigger only shows at xl and below */}
                      {dropdownItems.length > 0 && (
                        <CustomDropdown
                          trigger={
                            <Button
                              variant="ghost-clean"
                              size="sm"
                              className="p-0 max-xl:flex hidden items-center ml-2"
                            >
                              <MdKeyboardArrowDown
                                size={24}
                                className={`transition-transform duration-200 ${
                                  openDropdown === "dropdown-nav" ? "rotate-180" : ""
                                }`}
                              />
                            </Button>
                          }
                          content={
                            <div className="py-2">
                              {dropdownItems.map(item => (
                                <Link
                                  key={item.href}
                                  href={item.href}
                                  className={cn(
                                    "block px-4 py-2 font-medium text-text-secondary hover:text-text-primary hover:bg-surfaces-bg-hover",
                                    pathname === item.href && "text-text-primary font-bold",
                                  )}
                                  onClick={() => setOpenDropdown(null)}
                                >
                                  {item.label}
                                </Link>
                              ))}
                            </div>
                          }
                          dropdownId="dropdown-nav"
                          openDropdown={openDropdown}
                          setOpenDropdown={setOpenDropdown}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation links - visible above xl */}
              <div className="xl:flex hidden items-center gap-4">
                {linkItems.map(item => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      "text-text-secondary hover:text-text-primary font-semibold",
                      pathname === item.href && "text-text-primary font-bold",
                    )}
                    onClick={closeAllModals}
                  >
                    {item.label}
                  </Link>
                ))}
                <MenuDivider />
              </div>

              {/* Orders and Help dropdowns container */}
              <div className="flex items-center gap-4">
                {/* Orders dropdown - always visible if authenticated */}
                {ordersItem && ordersItem.type === "dropdown" && isAuthenticatedUser && (
                  <CustomDropdown
                    trigger={
                      <Button
                        variant="ghost-clean"
                        className="!text-medium text-text-secondary hover:text-text-primary group -mx-4"
                        onClick={e => {
                          e.stopPropagation();
                          router.push("/p2p-orders");
                        }}
                      >
                        <ordersItem.icon size={20} />
                        {ordersItem.label}
                        <MdKeyboardArrowDown
                          size={24}
                          className={`transition-transform duration-200 ${
                            openDropdown === "dropdown-orders" ? "rotate-180" : ""
                          }`}
                        />
                      </Button>
                    }
                    content={
                      <div className="p-4 h-auto max-h-[85dvh] overflow-y-auto custom-scrollbar">{ordersContent}</div>
                    }
                    dropdownId="dropdown-orders"
                    openDropdown={openDropdown}
                    setOpenDropdown={setOpenDropdown}
                  />
                )}

                {/* Help dropdown - moves to logo dropdown at navbar-bp */}
                {helpLink && helpLink.type === "dropdown" && (
                  <div className="max-navbar-bp:hidden">
                    <CustomDropdown
                      trigger={
                        <Button
                          variant="ghost-clean"
                          className="!text-medium text-text-secondary hover:text-text-primary group -mx-4"
                        >
                          <helpLink.icon size={20} />
                          {helpLink.label}
                          <MdKeyboardArrowDown
                            size={24}
                            className={`transition-transform duration-200 ${
                              openDropdown === "dropdown-help" ? "rotate-180" : ""
                            }`}
                          />
                        </Button>
                      }
                      content={
                        <div className="p-4">
                          {helpLink.children?.map(child => (
                            <Link
                              key={child.label}
                              href={child.href}
                              className="block px-4 py-2 text-text-secondary hover:text-text-primary"
                              onClick={() => setOpenDropdown(null)}
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      }
                      dropdownId="dropdown-help"
                      openDropdown={openDropdown}
                      setOpenDropdown={setOpenDropdown}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right section */}
          <div className="flex items-center gap-2">
            {!isAuthenticatedUser && !isAdminPage && <SignInButton />}
            {isAuthenticatedUser && (
              <>
                <div className="flex gap-3 items-center">
                  {!IS_MAINNET && (
                    <IconButton className="max-lg:hidden" onClick={() => setIsFaucetOpen(true)}>
                      <RiDropFill size={20} className="text-text-secondary" />
                      <span>Faucet</span>
                    </IconButton>
                  )}

                  <Link href="/gold-bars" onClick={closeAllModals}>
                    <IconButton size="sm" className="text-warning-500 text-small-2 max-lg:hidden">
                      <GoldBarsIcon size={20} />
                      <span>{isLoading ? <Skeleton className="h-4 w-4 inline-block" /> : `${goldBars}`}</span>
                    </IconButton>
                  </Link>

                  <div
                    className={cn(
                      "p-2 text-text-secondary bg-surfaces-bg-secondary hover:opacity-75 rounded-full relative cursor-pointer",
                    )}
                    onClick={() => setIsUserAccountOpen(true)}
                  >
                    <NetworkAvatar connectorIcon={activeAccount.connectorIcon} />
                  </div>

                  <Link href="/wallet" onClick={closeAllModals}>
                    <IconButton size="sm" className="!text-small">
                      <WalletIcon size={20} />
                      <span className="max-lg:hidden">{shortenAddress(activeAccount.address)}</span>
                    </IconButton>
                  </Link>
                </div>
              </>
            )}

            {isAuthenticatedUser && (
              <div className="flex items-center gap-2">
                <MenuDivider />

                {/* Notifications - Conditional Rendering */}
                {isDesktop ? (
                  <div className={cn(openDropdown === "dropdown-notifications" && "z-50")}>
                    <CustomDropdown
                      trigger={
                        <IconButton size="icon">
                          <NotificationIcon />
                        </IconButton>
                      }
                      content={<NotificationsModal notifications={notifications} />}
                      dropdownId="dropdown-notifications"
                      openDropdown={openDropdown}
                      setOpenDropdown={setOpenDropdown}
                    />
                  </div>
                ) : (
                  <Sheet open={isNotificationSheetOpen} onOpenChange={setIsNotificationSheetOpen}>
                    <SheetTrigger asChild>
                      <IconButton size="icon">
                        <NotificationIcon />
                      </IconButton>
                    </SheetTrigger>
                    <SheetContent side="top" className="w-full p-0 h-[100dvh] overflow-y-auto [&>button]:hidden">
                      <SheetTitle className="sr-only">Notifications</SheetTitle>
                      <NotificationsModal context="sheet" notifications={notifications} />
                    </SheetContent>
                  </Sheet>
                )}

                <CustomDropdown
                  trigger={
                    <IconButton size="icon" className="mb-1">
                      <CgMenuGridO size={20} />
                    </IconButton>
                  }
                  content={<Menu onCloseParentDropdown={() => setOpenDropdown(null)} />}
                  dropdownId="dropdown-menu"
                  openDropdown={openDropdown}
                  setOpenDropdown={setOpenDropdown}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {
        <>
          {" "}
          {!IS_MAINNET && <FaucetModal open={isFaucetOpen} onOpenChange={setIsFaucetOpen} />}
          {isUserAccountOpen && <UserAccountModal open={isUserAccountOpen} onOpenChange={setIsUserAccountOpen} />}
        </>
      }
    </nav>
  );
}
